import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import type { Speaker } from "src/types";

import type { RootState } from "@/redux/root-reducer";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch } from "@/utils/fetchJSON";
import { isError } from "@/utils/is-error";

import { getState } from "../helpers";
import { getIsFirstLoading, getIsLoading } from "../requests/requests-reducer";

const get = endpoints();

export const fetchSpeakers = createAsyncThunk<Speaker[], string>(
  "speakers/fetch",
  async (slug: string, { rejectWithValue }) => {
    const response = await authenticatedFetch.fetchJSON<Speaker[]>(
      get("SPEAKERS_API")(slug)
    );

    if (isError(response)) {
      toast.error(response.description, { toastId: response.description });
      return rejectWithValue(response);
    }
    return response;
  }
);

export const speakersAdapter = createEntityAdapter<Speaker>();

const speakersSlice = createSlice({
  name: "speakers",
  initialState: speakersAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchSpeakers.fulfilled, speakersAdapter.upsertMany);
  },
});

const { selectAll: getAllSpeakers } = speakersAdapter.getSelectors(
  (state: RootState) => state.speakers
);

const getIsSpeakersLoading = createSelector(
  getState,
  getAllSpeakers,
  (state, speakers) => {
    return getIsLoading(state, fetchSpeakers) && !speakers.length;
  }
);

const getIsSpeakersFirstLoading = createSelector(getState, (state) => {
  return getIsFirstLoading(state, fetchSpeakers);
});

// Selectors
export { getAllSpeakers, getIsSpeakersLoading, getIsSpeakersFirstLoading };

export default speakersSlice.reducer;
